import QuestionModel from "../QuestionModel";
import QuestionValue from "../QuestionValue";
import QuestionValueFraction from "../QuestionValueFraction";
import QuestionModule from "./QuestionModule";

export default class QuestionModulePlaceValue extends QuestionModule {
    constructor() {
        super();
    }

    create(data) {
        const question = super.create(data);
        switch (data.type) {
            case QuestionModel.TYPE_COMPARISON_FRACTION : {
                this.createComparisonFraction(question, data);
                break;
            }
            case QuestionModel.TYPE_COMPARISON : {
                this.createComparison(question, data);
                break;
            }
            case QuestionModel.TYPE_VALUE : {
                this.createValue(question, data);
                break;
            }
            case QuestionModel.TYPE_COMPOSE : {
                this.createCompose(question, data);
                break;
            }
            case QuestionModel.TYPE_ROUND : {
                this.createRound(question, data);
                break;
            }
        }
        return question;
    }

    createComparison(question, data) {
        question.lowest = P3.math.randomBool();

        if(data.dp === undefined) data.dp = 2;

        let k = 100;
        while (question.options.length < data.values && --k > 0) {
            const option = new QuestionValue(data.decimal ? parseFloat(P3.math.random(data.range.min, data.range.max).toFixed(data.dp)) : P3.math.randomInt(data.range.min, data.range.max));
            if (!question.options.length || question.options.every(x => x.value !== option.value)) {
                question.options.push(option);
            }
        }
        question.options.sort((a, b) => {
            return question.lowest ? b.value - a.value : a.value - b.value;
        });
        question.values = [question.options[0]];
        P3.utils.shuffleArray(question.options);
    }

    createComparisonFraction(question, data) {
        question.lowest = P3.math.randomBool();

        if (!data.range_denominator) {
            data.range_denominator = data.range;
        }

        //ensure either the same denominator, or that the numerator is 1 (bbc feedback)
        const numeratorIsOne = Math.random() < 0.5;
        let randomDenominator = P3.math.randomInt(data.range_denominator.min, data.range_denominator.max);
        if (randomDenominator === 1) randomDenominator++;

        let k = 1000;
        while (question.options.length < data.values && --k > 0) {
            const numerator = numeratorIsOne ? 1 : P3.math.randomInt(data.range.min, data.range.max);
            let denominator = numeratorIsOne ? P3.math.randomInt(data.range_denominator.min, data.range_denominator.max) : randomDenominator;

            if (denominator === 1) denominator++;

            if (numerator < denominator) {
                if (question.options.every(x => parseFloat(x.value) !== numerator / denominator))
                    question.options.push(new QuestionValueFraction(numerator, denominator));
            }
        }
        question.options.sort((a, b) => {
            return question.lowest ? b.value - a.value : a.value - b.value;
        });
        question.values = [question.options[0]];
        P3.utils.shuffleArray(question.options);
    }

    createValue(question, data) {
        let k = 10;
        let answer = P3.math.randomInt(data.range.min, data.range.max);

        question.values = [new QuestionValue(answer)];
        question.options = [new QuestionValue(answer)];
        while (question.options.length < data.values && k-- > 0) {
            const optionCharArray = ("" + answer).split("");
            //take first digit
            const firstDigit = optionCharArray[0];
            optionCharArray.splice(0, 1);//remove from array
            P3.utils.shuffleArray(optionCharArray);//shuffle arr;

            let optionString = firstDigit;
            for (let i = 0; i < optionCharArray.length; i++) {
                optionString += optionCharArray[i];
            }

            const optionValue = parseInt(optionString);
            const option = new QuestionValue(optionValue);

            if (!question.options.length || question.options.every(x => x.value !== option.value)) {
                question.options.push(option);
            }
        }

        while (question.options.length < 5) {
            const option = new QuestionValue(P3.math.randomInt(answer - 20, answer + 20));
            if (!question.options.length || question.options.every(x => x.value !== option.value)) {
                question.options.push(option);
            }
        }

        P3.utils.shuffleArray(question.options);
    }

    createCompose(question, data) {
        //generates a sum composed of individual parts (coefficient base pairs) that make up the final value
        const sumLength = data.values;

        let base = 1;
        let sum = 0;
        let k = 100;
        let coefficients = [];

        while (question.values.length < sumLength && --k > 0) {
            const coefficient = P3.math.randomInt(question.range.min, question.range.max);
            let current = coefficient * base;
            if (coefficients.indexOf(coefficient) === -1) {
                coefficients.push(coefficient);
                question.values.push(new QuestionValue(current));
                question.options.push(new QuestionValue(current));
                sum += current;
                base *= 10;
            }
        }

        question.values.splice(0, 0, new QuestionValue(sum));
        question.part = P3.utils.selectRandom(data.parts);
        question.length = sumLength;

        k = 100;
        const bases = [10, 100, 1000];
        while (question.options.length < 5 && --k > 0) {
            //pick random coefficient and a random base
            const option = new QuestionValue(P3.utils.selectRandom(coefficients) * P3.utils.selectRandom(bases));
            if (question.options.every(x => x.value !== option.value)) {
                question.options.push(option);
                base *= 10;
            }
        }

        P3.utils.shuffleArray(question.options);
    }

    createRound(question, data) {
        //Round the following number to the nearest [10/100/1000]{34,567}
        const rounding = data.rounding ? data.rounding : 10;//P3.math.randomInt(data.range.min, data.range.max);
        let displayed = P3.math.randomInt(data.range.min, data.range.max);
        if (displayed % 10 === 0) displayed++;//make sure it isn't already rounded

        const rounded = Math.round(displayed / rounding) * rounding;

        question.values.push(new QuestionValue(displayed));
        question.values.push(new QuestionValue(rounding));
        question.values.push(new QuestionValue(rounded));

        question.part = 2;

        question.options.push(new QuestionValue(rounded));

        let k = 100;
        while (question.options.length < 5 && --k > 0) {
            const option = new QuestionValue(Math.round((rounded + P3.math.randomInt(-rounding * 4, rounding * 4)) / rounding) * rounding);
            if (question.options.every(x => x.value !== option.value)) {
                question.options.push(option);
            }
        }
    }
}